import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { ModulePdfButton } from "../components/ModulePdfButton";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Brain, AlertTriangle, CloudRain, Zap, Repeat, HeartPulse, Compass,
  ArrowLeft, ChevronRight, ChevronLeft, BookOpen, Clock,
  CheckCircle, XCircle, HelpCircle, Send, Sparkles, Shield, ShieldCheck, Users, Target, HeartHandshake } from "lucide-react";
import { getPsychoeducationModules } from "../lib/psychoeducationData";
import { useI18n } from "../lib/i18n";
import { API } from "../App";
import { useNavigate } from "react-router-dom";

const ICON_MAP = {
  brain: Brain, "alert-triangle": AlertTriangle, "cloud-rain": CloudRain,
  zap: Zap, repeat: Repeat, "heart-pulse": HeartPulse, heartpulse: HeartPulse,
  compass: Compass,
};

const TheoryModulesPage = ({ user, logout }) => {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const PSYCHOEDUCATION_MODULES = getPsychoeducationModules(t);

  // Newer psychoeducation modules (from PsychoeducationPage) integrated here
  const NEWER_MODULES = [
    { id: "psychoed_tdah_child", title: t("psychoed.tdahChild.title", "Comprendre mon TDAH (Enfant)"), subtitle: t("psychoed.tdahChild.subtitle", "Un voyage pour mieux te connaitre !"), icon: Sparkles, color: "from-amber-400 to-orange-500", chapters: 5, duration: "20 min", isChild: true },
    { id: "psychoed_limits_child", title: t("psychoed.limitsChild.title", "Mon Jardin Secret - Les Limites (Enfant)"), subtitle: t("psychoed.limitsChild.subtitle", "Apprends a proteger ce qui est important pour toi !"), icon: Shield, color: "from-emerald-400 to-teal-500", chapters: 5, duration: "20 min", isChild: true },
    { id: "psychoed_limits_adult", title: t("psychoed.limitsAdult.title", "Communiquer ses Limites (Adulte)"), subtitle: t("psychoed.limitsAdult.subtitle", "Techniques assertives pour des relations saines"), icon: ShieldCheck, color: "from-slate-500 to-slate-700", chapters: 5, duration: "25 min", isChild: false },
    { id: "psychoed_schema_therapy", title: t("psychoed.schemaTherapy.title", "Therapie des Schemas"), subtitle: t("psychoed.schemaTherapy.subtitle", "Comprendre et transformer vos schemas (J. Young)"), icon: BookOpen, color: "from-indigo-500 to-purple-600", chapters: 13, duration: "60 min", isChild: false },
    { id: "psychoed_tcd", title: t("psychoed.tcd.title", "TCD (Linehan)"), subtitle: t("psychoed.tcd.subtitle", "Competences essentielles de la TCD"), icon: BookOpen, color: "from-teal-500 to-cyan-600", chapters: 10, duration: "50 min", isChild: false },
    { id: "psychoed_dissociation_trauma", title: t("psychoed.dissociationTrauma.title", "Dissociation et Trauma"), subtitle: t("psychoed.dissociationTrauma.subtitle", "Comprendre et traiter la dissociation et le trauma"), icon: BookOpen, color: "from-rose-500 to-red-600", chapters: 12, duration: "60 min", isChild: false },
    { id: "psychoed_toc", title: t("psychoed.toc.title", "Comprendre le TOC"), subtitle: t("psychoed.toc.subtitle", "Trouble Obsessionnel Compulsif : comprendre et agir"), icon: BookOpen, color: "from-amber-500 to-orange-600", chapters: 10, duration: "50 min", isChild: false },
    { id: "psychoed_borderline", title: t("psychoed.borderline.title", "Comprendre le Trouble Borderline"), subtitle: t("psychoed.borderline.subtitle", "Trouble de la Personnalite Borderline : comprendre pour mieux vivre"), icon: BookOpen, color: "from-fuchsia-500 to-purple-600", chapters: 12, duration: "60 min", isChild: false },
    { id: "psychoed_perfectionism", title: t("psychoed.perfectionism.title", "Perfectionnisme et excellencisme"), subtitle: t("psychoed.perfectionism.subtitle", "Viser haut sans s'épuiser : comprendre le perfectionnisme et cultiver l'excellence"), icon: Target, color: "from-teal-500 to-amber-500", chapters: 10, duration: "50 min", isChild: false },
    { id: "psychoed_perfectionism_ado", title: t("psychoed.perfectionismAdo.title", "Perfectionnisme et excellence (12-17 ans)"), subtitle: t("psychoed.perfectionismAdo.subtitle", "Viser haut sans te mettre la pression : comprendre le perfectionnisme et devenir « excellenciste »"), icon: Target, color: "from-indigo-500 to-amber-400", chapters: 8, duration: "30 min", isChild: false, isTeen: true },
    { id: "psychoed_grief", title: t("psychoed.grief.title", "Le processus du deuil"), subtitle: t("psychoed.grief.subtitle", "Comprendre le deuil, reconnaître quand il se complique et avancer avec l'approche TCC"), icon: HeartHandshake, color: "from-slate-500 to-amber-400", chapters: 10, duration: "50 min", isChild: false },
    { id: "psychoed_grief_ado", title: t("psychoed.griefAdo.title", "Vivre un deuil (12-17 ans)"), subtitle: t("psychoed.griefAdo.subtitle", "Comprendre ce qui se passe en toi quand tu perds quelqu'un, et trouver comment avancer sans oublier"), icon: HeartHandshake, color: "from-indigo-500 to-amber-400", chapters: 8, duration: "30 min", isChild: false, isTeen: true },
    { id: "psychoed_erectile_dysfunction", title: t("psychoed.erectileDysfunction.title", "Dysfonction érectile et anxiété de performance"), subtitle: t("psychoed.erectileDysfunction.subtitle", "Comprendre l'anxiété de performance et pratiquer la focalisation sensorielle"), icon: HeartPulse, color: "from-indigo-500 to-violet-600", chapters: 11, duration: "55 min", isChild: false },
  ];
  const [selectedModule, setSelectedModule] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [psychoAssignments, setPsychoAssignments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignModuleId, setAssignModuleId] = useState(null);
  const [assignPatientId, setAssignPatientId] = useState("");

  const isTherapist = user?.role === "therapist";

  const fetchAssignments = async () => {
    try {
      const res = await axios.get(`${API}/psychoeducation/assignments`, { withCredentials: true });
      setPsychoAssignments(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    fetchAssignments();
    if (isTherapist) {
      axios.get(`${API}/therapist/patients`, { withCredentials: true })
        .then(res => setPatients(res.data.filter(p => p.is_active)))
        .catch(console.error);
    }
  }, []);

  const assignedModuleIds = psychoAssignments.map(a => a.module_id);
  const visibleModules = isTherapist
    ? PSYCHOEDUCATION_MODULES
    : PSYCHOEDUCATION_MODULES.filter(m => assignedModuleIds.includes(m.id));

  const handleAssignModule = async () => {
    if (!assignModuleId || !assignPatientId) return;
    try {
      await axios.post(`${API}/psychoeducation/assign`, { module_id: assignModuleId, patient_id: assignPatientId }, { withCredentials: true });
      toast.success(t("psychoeducation.moduleAssigned", "Module assigné"));
      setAssignPatientId("");
      fetchAssignments();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleUnassignModule = async (assignmentId) => {
    try {
      await axios.delete(`${API}/psychoeducation/assign/${assignmentId}`, { withCredentials: true });
      toast.success(t("psychoeducation.moduleUnassigned", "Module retiré"));
      fetchAssignments();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const getModuleAssignments = (moduleId) => psychoAssignments.filter(a => a.module_id === moduleId);
  const getPatientName = (patientId) => patients.find(p => p.user_id === patientId)?.name || patientId;

  const openAssignDialog = (moduleId) => { setAssignModuleId(moduleId); setAssignPatientId(""); setAssignDialogOpen(true); };

  const resetModule = () => {
    setSelectedModule(null);
    setCurrentSection(0);
    setShowQuiz(false);
    setQuizAnswers({});
    setQuizSubmitted(false);
  };

  const handleQuizAnswer = (qIdx, aIdx) => {
    if (quizSubmitted) return;
    setQuizAnswers((prev) => ({ ...prev, [qIdx]: aIdx }));
  };

  const submitQuiz = () => setQuizSubmitted(true);

  if (selectedModule) {
    const mod = selectedModule;
    const Icon = ICON_MAP[mod.icon] || BookOpen;
    const totalSections = mod.sections.length;

    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="space-y-6 max-w-3xl mx-auto" data-testid="psychoeducation-module">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={resetModule} data-testid="back-to-modules">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="font-heading text-2xl font-semibold">{mod.title}</h1>
              <p className="text-sm text-muted-foreground">{mod.subtitle}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="w-3.5 h-3.5" /> {mod.duration}</span>
              <ModulePdfButton moduleId={mod.id} iconOnly />
            </div>
          </div>

          <div className="flex gap-1.5">
            {mod.sections.map((_, i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full cursor-pointer transition-colors ${i <= currentSection ? "bg-primary" : "bg-muted"}`} onClick={() => { setShowQuiz(false); setCurrentSection(i); }} />
            ))}
            {mod.quiz?.length > 0 && (
              <div className={`h-1.5 flex-1 rounded-full cursor-pointer transition-colors ${showQuiz ? "bg-primary" : "bg-muted"}`} onClick={() => setShowQuiz(true)} />
            )}
          </div>
          <p className="text-xs text-center text-muted-foreground">
            {showQuiz ? t("psychoeducation.quiz") : `${t("psychoeducation.section")} ${currentSection + 1} / ${totalSections}`}
          </p>

          {mod.image && (
            <Card className="overflow-hidden">
              <CardContent className="p-4 flex justify-center bg-background">
                <img
                  src={mod.image}
                  alt={mod.title}
                  className="max-w-sm w-full h-auto"
                  data-testid="cbt-model-image"
                />
              </CardContent>
            </Card>
          )}

          {!showQuiz ? (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Icon className="w-5 h-5 text-primary" />
                  {mod.sections[currentSection].title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Section illustration */}
                {mod.sectionImage && (
                  <div className="mb-5 rounded-xl overflow-hidden">
                    <img
                      src={mod.sectionImage}
                      alt={mod.sections[currentSection].title}
                      className="w-full h-48 sm:h-56 object-cover"
                      loading="lazy"
                    />
                  </div>
                )}
                <p className="text-[15px] leading-relaxed text-foreground/90">{mod.sections[currentSection].content}</p>
                <div className="flex justify-between pt-8 mt-6 border-t">
                  <Button variant="outline" size="sm" disabled={currentSection === 0} onClick={() => setCurrentSection((p) => p - 1)}>
                    <ChevronLeft className="w-4 h-4 mr-1" /> {t("psychoeducation.previous") || "Previous"}
                  </Button>
                  {currentSection < totalSections - 1 ? (
                    <Button size="sm" onClick={() => setCurrentSection((p) => p + 1)}>
                      {t("psychoeducation.next") || "Next"} <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  ) : mod.quiz?.length > 0 ? (
                    <Button size="sm" onClick={() => setShowQuiz(true)}>
                      {t("psychoeducation.goToQuiz")} <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  ) : (
                    <Button size="sm" onClick={resetModule}>
                      {t("common.save")} <CheckCircle className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-xl flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-primary" />
                    {t("psychoeducation.quizTitle")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-8">
                  {mod.quiz.map((q, qIdx) => (
                    <div key={qIdx} className="space-y-3">
                      <p className="font-medium">{q.question}</p>
                      <div className="space-y-2">
                        {q.options.map((opt, aIdx) => {
                          const selected = quizAnswers[qIdx] === aIdx;
                          const isCorrect = q.correct === aIdx;
                          let variant = "outline";
                          if (quizSubmitted && selected && isCorrect) variant = "default";
                          if (quizSubmitted && selected && !isCorrect) variant = "destructive";
                          if (quizSubmitted && !selected && isCorrect) variant = "default";
                          return (
                            <Button key={aIdx} variant={selected && !quizSubmitted ? "secondary" : variant} className={`w-full justify-start text-left h-auto py-3 px-4 ${quizSubmitted && isCorrect ? "border-green-500 bg-green-50 text-green-800" : ""} ${quizSubmitted && selected && !isCorrect ? "border-red-500 bg-red-50 text-red-800" : ""}`} onClick={() => handleQuizAnswer(qIdx, aIdx)}>
                              <span className="flex items-center gap-2">
                                {quizSubmitted && isCorrect && <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />}
                                {quizSubmitted && selected && !isCorrect && <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />}
                                {!quizSubmitted && <span className="w-5 h-5 rounded-full border-2 flex items-center justify-center text-xs flex-shrink-0">{String.fromCharCode(65 + aIdx)}</span>}
                                <span>{opt}</span>
                              </span>
                            </Button>
                          );
                        })}
                      </div>
                      {quizSubmitted && <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">{q.explanation}</div>}
                    </div>
                  ))}
                  {!quizSubmitted ? (
                    <Button className="w-full" onClick={submitQuiz} disabled={Object.keys(quizAnswers).length < mod.quiz.length}>
                      {t("psychoeducation.checkAnswers")}
                    </Button>
                  ) : (
                    <div className="flex justify-between pt-4 border-t">
                      <Button variant="outline" onClick={resetModule}>
                        <ArrowLeft className="w-4 h-4 mr-2" /> {t("psychoeducation.backToModules")}
                      </Button>
                      <div className="text-sm font-medium text-primary flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        {Object.entries(quizAnswers).filter(([qIdx, aIdx]) => mod.quiz[qIdx].correct === aIdx).length} / {mod.quiz.length} {t("psychoeducation.correct")}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Sources */}
          {mod.sources && mod.sources.length > 0 && (
            <Card className="border-dashed">
              <CardContent className="pt-4 pb-3">
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  {locale === "fr" ? "Sources" : locale === "es" ? "Fuentes" : "References"}
                </p>
                <ul className="space-y-1">
                  {mod.sources.map((src, i) => (
                    <li key={i} className="text-xs text-muted-foreground/80 leading-relaxed">
                      {src}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="psychoeducation-page">
        <div className="flex items-center gap-4">
          <Link to={user?.role === "therapist" ? "/therapist" : "/patient"}>
            <Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button>
          </Link>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("psychoeducation.title")}</h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("psychoeducation.pageSubtitle")}</p>
          </div>
        </div>

        <Card className="border-l-4 border-l-primary bg-primary/[0.03]">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <BookOpen className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-sm uppercase tracking-wide text-primary mb-1">{t("psychoeducation.aboutModule")}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{t("psychoeducation.aboutModuleDesc")}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Newer Modules Section */}
        {(isTherapist || NEWER_MODULES.some(m => assignedModuleIds.includes(m.id))) && (
          <>
            <h2 className="font-heading text-lg font-semibold text-foreground mt-2">{t("psychoeducation.newerModulesTitle", "Modules approfondis")}</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {(isTherapist ? NEWER_MODULES : NEWER_MODULES.filter(m => assignedModuleIds.includes(m.id))).map((mod) => {
                const Icon = mod.icon;
                const modAssignments = getModuleAssignments(mod.id);
                return (
                  <Card key={mod.id} className="cursor-pointer hover:shadow-md transition-shadow group" data-testid={`module-${mod.id}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4" onClick={() => navigate(`/exercises/psychoed/${mod.id}`)}>
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${mod.color} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-semibold text-base group-hover:text-primary transition-colors">{mod.title}</h3>
                          <p className="text-xs text-muted-foreground mb-1">{mod.subtitle}</p>
                          <div className="flex items-center gap-3 mt-3">
                            <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> {mod.duration}</span>
                            <span className="text-xs text-muted-foreground flex items-center gap-1"><BookOpen className="w-3 h-3" /> {mod.chapters} {t("psychoeducation.sections")}</span>
                            {mod.isTeen && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-200" data-testid={`teen-badge-${mod.id}`}>12-17 {t("psychoeducation.years", "ans")}</span>}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                      </div>
                      <div className="mt-4 pt-3 border-t flex gap-2">
                        <ModulePdfButton moduleId={mod.id} className={isTherapist ? "" : "w-full"} />
                        {isTherapist && (
                          <Button variant="outline" size="sm" className="flex-1 gap-2" onClick={(e) => { e.stopPropagation(); openAssignDialog(mod.id); }} data-testid={`assign-psycho-${mod.id}`}>
                            <Send className="w-3.5 h-3.5" /> {t("library.assign", "Assigner")}
                            {modAssignments.length > 0 && (
                              <span className="ml-auto text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Users className="w-3 h-3" /> {modAssignments.length}
                              </span>
                            )}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}

        {/* Original Theory Modules Section */}
        <h2 className="font-heading text-lg font-semibold text-foreground mt-2">{t("psychoeducation.classicModulesTitle", "Fondements theoriques")}</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {visibleModules.length > 0 ? visibleModules.map((mod) => {
            const Icon = ICON_MAP[mod.icon] || BookOpen;
            const modAssignments = getModuleAssignments(mod.id);
            return (
              <Card key={mod.id} className="cursor-pointer hover:shadow-md transition-shadow group overflow-hidden" data-testid={`module-${mod.id}`}>
                {/* Module illustration thumbnail */}
                {(mod.sectionImage || mod.image) && (
                  <div className="w-full h-32 overflow-hidden">
                    <img src={mod.sectionImage || mod.image} alt={mod.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                  </div>
                )}
                <CardContent className="pt-4">
                  <div className="flex items-start gap-4" onClick={() => setSelectedModule(mod)}>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${mod.color} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-semibold text-base group-hover:text-primary transition-colors">{mod.title}</h3>
                      <p className="text-xs text-muted-foreground mb-1">{mod.subtitle}</p>
                      <p className="text-sm text-muted-foreground line-clamp-2">{mod.description}</p>
                      <div className="flex items-center gap-3 mt-3">
                        <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> {mod.duration}</span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1"><BookOpen className="w-3 h-3" /> {mod.sections.length} {t("psychoeducation.sections")}</span>
                        {mod.quiz?.length > 0 && <span className="text-xs text-muted-foreground flex items-center gap-1"><HelpCircle className="w-3 h-3" /> {t("psychoeducation.quiz")}</span>}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                  </div>
                  <div className="mt-4 pt-3 border-t flex gap-2">
                    <ModulePdfButton moduleId={mod.id} className={isTherapist ? "" : "w-full"} />
                    {isTherapist && (
                      <Button variant="outline" size="sm" className="flex-1 gap-2" onClick={(e) => { e.stopPropagation(); openAssignDialog(mod.id); }} data-testid={`assign-psycho-${mod.id}`}>
                        <Send className="w-3.5 h-3.5" /> {t("library.assign", "Assigner")}
                        {modAssignments.length > 0 && (
                          <span className="ml-auto text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Users className="w-3 h-3" /> {modAssignments.length}
                          </span>
                        )}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          }) : (
            <Card className="md:col-span-2">
              <CardContent className="p-8 text-center text-muted-foreground">
                <p>{t("psychoeducation.noModulesAssigned", "Aucun module ne vous a encore ete assigne. Votre therapeute vous en attribuera prochainement.")}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Assign Dialog (Therapist) - shows assigned patients inside */}
        {isTherapist && (
          <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">{t("psychoeducation.assignModule", "Assigner un module")}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Current assignments for this module */}
                {assignModuleId && getModuleAssignments(assignModuleId).length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">{t("psychoeducation.currentAssignments", "Patients assignes")}</Label>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {getModuleAssignments(assignModuleId).map(a => (
                        <div key={a.assignment_id} className="flex items-center justify-between text-sm px-3 py-2 rounded-lg bg-muted/50">
                          <span className="flex items-center gap-2"><Users className="w-3.5 h-3.5 text-muted-foreground" />{getPatientName(a.patient_id)}</span>
                          <Button type="button" variant="ghost" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 px-2 text-xs" onClick={() => handleUnassignModule(a.assignment_id)} data-testid={`unassign-psycho-${a.assignment_id}`}>
                            {t("library.remove", "Retirer")}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>{t("psychoeducation.addPatient", "Ajouter un patient")}</Label>
                  <Select value={assignPatientId} onValueChange={setAssignPatientId}>
                    <SelectTrigger data-testid="psycho-patient-select"><SelectValue placeholder={t("library.selectPatient", "Selectionner un patient")} /></SelectTrigger>
                    <SelectContent>{patients.map(p => (<SelectItem key={p.user_id} value={p.user_id}>{p.name}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <Button className="w-full" onClick={() => { handleAssignModule(); setAssignDialogOpen(false); }} disabled={!assignPatientId} data-testid="submit-psycho-assignment">
                  <Send className="w-4 h-4 mr-2" /> {t("library.assignTheExercise", "Assigner")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TheoryModulesPage;
